/**
 * The frame every step of the text creation flow sits in (handoff §0.3, §19.0).
 *
 * Mobile: a full-screen column over the app (no bottom nav, which the route
 * hides): the flow top strip (× or ‹, the title, «N из 3»), a scrolling body and
 * a pinned bottom bar. Desktop: the app's own screen-header bar carries only back
 * and identity (§19.0, no new header targets), the body is a centred column and
 * the actions live in it.
 */
import { useMemo, type ReactNode } from 'react'
import { ChevronLeft, X } from 'lucide-react'
import { useScreenHeader } from '../../hooks/useScreenHeader'
import { useLocale } from '../../i18n/context'
import { CONTROL_SURFACE, suppressTouchContextMenu } from '../../utils/controlSurface'

interface Props {
  /** The creation flow's step; omitted for the edit mode's own screens, which are not steps. */
  step?: 1 | 2 | 3
  /** Replaces «N из 3» at the strip's right (edit mode's «Готово»). */
  trailing?: ReactNode
  testId?: string
  /** «Слова из текста» or «Новый текст». */
  title: string
  /** The strip's left button: × on step 1, ‹ on steps 2 and 3. */
  leading: 'close' | 'back'
  onLeave: () => void
  /** What the desktop back link names: «Библиотека», «Главная», «Шаг 1» … */
  backLabel: string
  /** Desktop only: the bar's subtitle («Шаг 1 из 3 · источник»). */
  subtitle: string
  /** Mobile only: the pinned bottom bar. Desktop pages put their actions in the body. */
  footer?: ReactNode
  /** The footer brings its own bar (edit mode's change strip): no padding or shadow around it. */
  footerBare?: boolean
  /** Desktop body width: step 1 is 720, step 3 is 600, step 2 is wider. */
  maxWidth: number
  children: ReactNode
}

const FRAME_CSS = `
.tf-frame{position:fixed;inset:0;z-index:40;display:flex;flex-direction:column;background:var(--bg-page);font-family:var(--font-ui)}
.tf-body{flex:1;min-height:0;overflow-y:auto;overscroll-behavior:contain}
@media(min-width:768px){
  .tf-frame{position:static;inset:auto;z-index:auto;display:block;background:transparent}
  .tf-strip,.tf-footer{display:none!important}
  .tf-body{overflow:visible;flex:none}
}
`

export default function FlowFrame({
  step, trailing, testId, title, leading, onLeave, backLabel, subtitle, footer, footerBare, maxWidth, children,
}: Props) {
  const { tr } = useLocale()
  const tf = tr.textFlow

  useScreenHeader(
    useMemo(
      () => ({ back: { label: backLabel, onClick: onLeave }, title, subtitle }),
      [backLabel, onLeave, title, subtitle],
    ),
  )

  return (
    // A surface of controls: no OS selection or Touch to Search on its labels and
    // buttons; the step 1 text area and the step 3 title keep normal selection.
    <div
      className={`tf-frame ${CONTROL_SURFACE}`}
      data-testid={testId ?? `text-flow-step-${step}`}
      style={{ ['--tf-max' as string]: `${maxWidth}px` }}
      onContextMenu={suppressTouchContextMenu}
    >
      <style>{FRAME_CSS}</style>
      <header
        className="tf-strip"
        style={{
          height: 52, flexShrink: 0, display: 'flex', alignItems: 'center', gap: 4,
          padding: '0 16px 0 4px', background: 'var(--bg-chrome)',
          borderBottom: '1px solid var(--border-chrome)',
        }}
      >
        <button
          type="button"
          onClick={onLeave}
          aria-label={leading === 'close' ? tf.close : tf.back}
          style={{
            width: 44, height: 44, display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-primary)',
          }}
        >
          {leading === 'close' ? <X size={22} /> : <ChevronLeft size={24} />}
        </button>
        <h1 style={{ flex: 1, margin: 0, fontSize: 15, fontWeight: 600, color: 'var(--text-primary)' }}>
          {title}
        </h1>
        {trailing ?? (step && <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{tf.stepOf(step)}</span>)}
      </header>

      <div className="tf-body">
        {/* Fills the scroll area, so a step's own content can stretch to the bottom bar (step 1's text area). */}
        <div style={{ maxWidth: `var(--tf-max)`, margin: '0 auto', minHeight: '100%', display: 'flex', flexDirection: 'column' }}>
          {children}
        </div>
      </div>

      {footer && (
        <footer
          className="tf-footer"
          style={footerBare ? { flexShrink: 0 } : {
            flexShrink: 0, background: 'var(--bg-chrome)',
            borderTop: '1px solid var(--border-chrome)',
            boxShadow: '0 -4px 12px rgba(0,0,0,0.04)',
            padding: '14px 16px calc(16px + env(safe-area-inset-bottom, 0px))',
          }}
        >
          {footer}
        </footer>
      )}
    </div>
  )
}
